/**
 * @file order-items-edit.ts
 * @description Когда менеджеру показывать редактор состава заказа
 * (phase19.15 шаг 4) — клиентское зеркало `order-items-edit-rules.ts` сервера.
 *
 * Зачем зеркало, а не «нажми и узнай»: правило юридическое (подтверждённая
 * клиентом сумма и оплаченный заказ — документы), и кнопка «Изменить состав»
 * на замороженном заказе — это обещание, которое интерфейс не может сдержать.
 * Сервер всё равно ответит 422 `order_items_locked`, поэтому клиент обязан
 * знать те же условия, а не догадываться о них.
 *
 * Чистые функции без запросов — проверяются юнит-тестом (order-items-edit.test.ts).
 */

import type { DeliveryAgreementStatus } from "./api-delivery-agreement";
import type { OrderPaymentStatus, OrderStageKind } from "./api-orders";

/** Машинная причина заморозки — те же коды, что в `details.reason` сервера. */
export type OrderItemsLockReason = "paid" | "agreement_confirmed" | "stage";

export interface OrderItemsEditability {
  editable: boolean;
  reason: OrderItemsLockReason | null;
  /** Объяснение менеджеру; `null` — править можно. */
  message: string | null;
}

/**
 * Стадии обычного заказа, на которых состав ещё не заморожен: денег нет,
 * сборка не началась.
 */
export const EDITABLE_ORDER_STAGES: readonly OrderStageKind[] = [
  "CREATED",
  "AWAITING_PAYMENT",
];

/** Тексты заморозки — реестром: те же формулировки менеджер увидит в 422. */
export const ORDER_ITEMS_LOCK_MESSAGE: Record<OrderItemsLockReason, string> = {
  agreement_confirmed:
    "Клиент письменно подтвердил итоговую сумму — состав заморожен. " +
    "Нужны изменения: отмените заказ и оформите новый.",
  paid:
    "Заказ оплачен — состав менять нельзя. Оформите возврат или создайте " +
    "новый заказ на доплату.",
  stage: "Состав заказа на этой стадии менять нельзя.",
};

/** Что знает карточка заказа о своём состоянии. */
export interface OrderItemsEditContext {
  stageKind: OrderStageKind | null;
  /** Сводный статус оплаты заказа: всё, кроме `NONE`, означает «деньги были». */
  paymentStatus: OrderPaymentStatus;
  /** Заявка «по договорённости» (F30) — есть отметка о приёме заявки. */
  isAgreement: boolean;
  /** Клиент нажал «Подтверждаю». */
  agreementConfirmed: boolean;
}

/**
 * Можно ли прямо сейчас менять состав заказа. Порядок проверок повторяет
 * сервер: сначала то, что менеджер уже не изменит (согласие клиента, деньги),
 * и только потом — стадия.
 */
export function resolveOrderItemsEditability(
  ctx: OrderItemsEditContext,
): OrderItemsEditability {
  if (ctx.isAgreement && ctx.agreementConfirmed) {
    return lock("agreement_confirmed");
  }
  if (ctx.paymentStatus !== "NONE") return lock("paid");

  const allowed: readonly OrderStageKind[] = ctx.isAgreement
    ? ["PENDING_DELIVERY_AGREEMENT"]
    : EDITABLE_ORDER_STAGES;
  if (ctx.stageKind !== null && allowed.includes(ctx.stageKind)) {
    return { editable: true, reason: null, message: null };
  }
  return {
    editable: false,
    reason: "stage",
    message: stageLockMessage(ctx.stageKind, ctx.isAgreement),
  };
}

/** Собрать контекст из карточки заказа и состояния заявки. */
export function orderItemsEditContext(order: {
  stage: { kind: OrderStageKind } | null;
  paymentStatus: OrderPaymentStatus;
}, agreement: { status: DeliveryAgreementStatus; requestedAt: string | null; confirmedAt: string | null } | null): OrderItemsEditContext {
  return {
    stageKind: order.stage?.kind ?? null,
    paymentStatus: order.paymentStatus,
    isAgreement: agreement?.requestedAt != null,
    agreementConfirmed: agreement?.confirmedAt != null,
  };
}

function lock(reason: OrderItemsLockReason): OrderItemsEditability {
  return { editable: false, reason, message: ORDER_ITEMS_LOCK_MESSAGE[reason] };
}

/** Почему стадия не даёт править — менеджер должен понять без справочника. */
function stageLockMessage(
  stageKind: OrderStageKind | null,
  isAgreement: boolean,
): string {
  if (stageKind === null) return "У заказа не определена стадия — состав менять нельзя.";
  switch (stageKind) {
    case "PACKAGING":
    case "PACKED":
      return "Заказ уже в сборке — состав менять нельзя.";
    case "HANDED_TO_CARRIER":
    case "IN_TRANSIT":
    case "ARRIVED_AT_PICKUP":
    case "READY_FOR_PICKUP":
    case "DELIVERED":
      return "Заказ уже отгружен — состав менять нельзя.";
    case "COMPLETED":
    case "CANCELLED":
    case "RETURN":
      return "Заказ закрыт — состав менять нельзя.";
    case "PAID":
      return "Заказ оплачен — состав менять нельзя.";
    default:
      return isAgreement
        ? "Менять состав заявки можно только на стадии «Согласование доставки»."
        : "Менять состав можно только до оплаты — на стадиях «Новый» и «Ожидание оплаты».";
  }
}
